import React, { useState, useEffect } from 'react';
import PatientSidebar from './PatientSidebar';
import { Link } from 'react-router-dom';
import { useBaseUrl } from '../utils/utils';
import localForage from 'localforage';

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface DashboardStats {
    active_requests: number;
    upcoming_appointments: number;
    telehealth_sessions: number;
    collaborations: number;
    pending_payments: number;
    trends: {
        requests_change: string;
        appointments_change: string;
        telehealth_change: string;
        collaborations_change: string;
        payments_change: string;
    };
}

interface RecentActivity {
    id: string;
    type: 'service_request' | 'appointment' | 'telehealth' | 'collaboration' | 'consent' | 'payment';
    title: string;
    description: string;
    status: 'pending' | 'confirmed' | 'completed' | 'approved' | 'active' | 'cancelled';
    timestamp: string;
    related_id?: string;
}

const ACTIVITY_ICONS: Record<string, string> = {
    service_request: 'bx bx-clipboard',
    appointment: 'bx bx-calendar',
    telehealth: 'bx bx-video',
    collaboration: 'bx bx-network-chart',
    consent: 'bx bx-check-shield',
    payment: 'bx bx-credit-card',
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
    pending: { bg: 'rgba(245,158,11,0.1)', text: '#d97706' },
    confirmed: { bg: 'rgba(59,130,246,0.1)', text: '#2563eb' },
    completed: { bg: 'rgba(16,185,129,0.1)', text: '#059669' },
    approved: { bg: 'rgba(16,185,129,0.1)', text: '#059669' },
    active: { bg: ACCENT_SOFT, text: ACCENT },
    cancelled: { bg: 'rgba(244,63,94,0.1)', text: '#e11d48' },
};

const PatientDashboard: React.FC = () => {
    const [patientName, setPatientName] = useState<string>('');
    const [stats, setStats] = useState<DashboardStats>({
        active_requests: 0, upcoming_appointments: 0, telehealth_sessions: 0, collaborations: 0, pending_payments: 0,
        trends: { requests_change: '0', appointments_change: '0', telehealth_change: '0', collaborations_change: '0', payments_change: '0' }
    });
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [patientId, setPatientId] = useState<string | null>(null);
    const baseUrl = useBaseUrl();

    useEffect(() => {
        document.title = "Patient Dashboard - Uzima Healthcare";
        initializeDashboard();
    }, []);

    const initializeDashboard = async () => {
        try {
            setLoading(true);
            const storedPatientId = await localForage.getItem("patient_id");
            if (!storedPatientId) throw new Error('Patient ID not found');
            setPatientId(storedPatientId as string);
            await Promise.all([
                fetchDashboardStats(storedPatientId as string),
                fetchRecentActivity(storedPatientId as string),
                fetchPatientProfile(storedPatientId as string)
            ]);
        } catch (error) {
            console.error('Error initializing dashboard:', error);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboardStats = async (pid: string) => {
        try {
            const res = await fetch(`${baseUrl}users/api/patient/dashboard/stats/?patient_id=${pid}`, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await localForage.getItem('access_token')}` }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setStats(data.stats || stats);
        } catch (e) { console.error('Stats error:', e); }
    };

    const fetchRecentActivity = async (pid: string) => {
        try {
            const res = await fetch(`${baseUrl}users/api/patient/dashboard/activity/?patient_id=${pid}`, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await localForage.getItem('access_token')}` }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setRecentActivity(data.activities || []);
        } catch (e) { console.error('Activity error:', e); }
    };

    const fetchPatientProfile = async (pid: string) => {
        try {
            const res = await fetch(`${baseUrl}users/api/patient/profile/${pid}/`, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await localForage.getItem('access_token')}` }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setPatientName(data.first_name || 'Patient');
        } catch (e) { console.error('Profile error:', e); setPatientName('Patient'); }
    };

    const refreshDashboard = () => { if (patientId) initializeDashboard(); };

    /* ─── Loading / Error states ─── */
    if (loading) {
        return (
            <div style={{ display: 'flex', minHeight: '100vh', background: '#fafbfc' }}>
                <PatientSidebar />
                <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="sc-spinner"></div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', minHeight: '100vh', background: '#fafbfc' }}>
                <PatientSidebar />
                <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(244,63,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="bx bx-error" style={{ fontSize: '1.5rem', color: '#e11d48' }}></i>
                    </div>
                    <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)' }}>{error}</p>
                    <button className="sc-btn sc-btn-primary" onClick={refreshDashboard}><i className="bx bx-refresh"></i> Try Again</button>
                </main>
            </div>
        );
    }

    /* ─── Main Render ─── */
    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#fafbfc' }}>
            <PatientSidebar />
            <main style={{ flex: 1, padding: '28px 32px', maxWidth: 1200, margin: '0 auto' }}>
                {/* Welcome */}
                <div style={{ marginBottom: 28 }}>
                    <h2 style={{ fontWeight: 800, color: 'var(--uzima-shell-text, #0f172a)', fontSize: '1.5rem', marginBottom: 4 }}>
                        Welcome back, {patientName}!
                    </h2>
                    <p style={{ color: 'var(--uzima-shell-text-muted, #94a3b8)', fontSize: '0.88rem', margin: 0 }}>
                        Here's your health overview for today.
                    </p>
                </div>

                {/* Quick Actions */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14, marginBottom: 28 }}>
                    {[
                        { label: 'Request Service', icon: 'bx bx-plus-circle', path: '/patient/service-requests', cls: 'teal' },
                        { label: 'Appointments', icon: 'bx bx-calendar', path: '/patient/appointments', cls: 'blue' },
                        { label: 'Telehealth', icon: 'bx bx-video', path: '/patient/telehealth', cls: 'violet' },
                        { label: 'My EHR', icon: 'bx bx-file', path: '/patient/dashboard/ehr', cls: 'amber' },
                        { label: 'Collaborations', icon: 'bx bx-network-chart', path: '/patient/collaborations', cls: 'rose' },
                    ].map((item, i) => (
                        <Link key={i} to={item.path} className="sc-card" style={{ textDecoration: 'none', display: 'block' }}>
                            <div className="sc-card-body" style={{ textAlign: 'center', padding: '20px 12px' }}>
                                <div className={`sc-stat-icon ${item.cls}`} style={{ margin: '0 auto 10px', width: 40, height: 40, borderRadius: 10 }}>
                                    <i className={item.icon} style={{ fontSize: '1.1rem' }}></i>
                                </div>
                                <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--uzima-shell-text)' }}>{item.label}</div>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Stats */}
                <div className="sc-stats" style={{ marginBottom: 28 }}>
                    {[
                        { label: 'Active Requests', val: stats.active_requests, trend: stats.trends.requests_change, icon: 'bx bx-clipboard', cls: 'teal' },
                        { label: 'Appointments', val: stats.upcoming_appointments, trend: stats.trends.appointments_change, icon: 'bx bx-calendar', cls: 'blue' },
                        { label: 'Telehealth', val: stats.telehealth_sessions, trend: stats.trends.telehealth_change, icon: 'bx bx-video', cls: 'violet' },
                        { label: 'Collaborations', val: stats.collaborations, trend: stats.trends.collaborations_change, icon: 'bx bx-network-chart', cls: 'amber' },
                        { label: 'Pending Payments', val: stats.pending_payments, trend: stats.trends.payments_change, icon: 'bx bx-credit-card', cls: 'rose' },
                    ].map((s, i) => {
                        const trendVal = parseInt(s.trend);
                        return (
                            <div className="sc-stat" key={i}>
                                <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                <div className="sc-stat-value">{s.val}</div>
                                <div className="sc-stat-label">{s.label}</div>
                                {trendVal !== 0 && (
                                    <span className={`sc-stat-trend ${trendVal > 0 ? 'up' : 'down'}`}>
                                        <i className={`bx ${trendVal > 0 ? 'bx-trending-up' : 'bx-trending-down'}`}></i> {trendVal > 0 ? '+' : ''}{s.trend}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Two-column: Activity + Quick Access */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
                    {/* Recent Activity */}
                    <div className="sc-card">
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--uzima-shell-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--uzima-shell-text)', fontSize: '0.88rem' }}>
                                <i className="bx bx-time-five me-2" style={{ color: ACCENT }}></i>Recent Activity
                            </h6>
                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={refreshDashboard}><i className="bx bx-refresh"></i></button>
                        </div>
                        <div className="sc-card-body" style={{ padding: '16px 20px' }}>
                            {recentActivity.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                                        <i className="bx bx-time-five" style={{ fontSize: '1.3rem', color: ACCENT }}></i>
                                    </div>
                                    <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)', fontSize: '0.86rem', marginBottom: 4 }}>No recent activity</p>
                                    <p style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>Your activities will appear here</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {recentActivity.map((item, idx) => {
                                        const sc = STATUS_COLORS[item.status] || STATUS_COLORS.active;
                                        return (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(15,23,42,0.015)' }}>
                                                <div style={{ width: 36, height: 36, borderRadius: 8, background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <i className={ACTIVITY_ICONS[item.type] || 'bx bx-info-circle'} style={{ fontSize: '1rem', color: ACCENT }}></i>
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--uzima-shell-text)' }}>{item.title}</div>
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{item.description} · {new Date(item.timestamp).toLocaleDateString()}</div>
                                                </div>
                                                <span style={{ padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.text, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Access */}
                    <div className="sc-card">
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--uzima-shell-border)' }}>
                            <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--uzima-shell-text)', fontSize: '0.88rem' }}>
                                <i className="bx bx-grid-alt me-2" style={{ color: ACCENT }}></i>Quick Access
                            </h6>
                        </div>
                        <div className="sc-card-body" style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[
                                { label: 'Service Requests', icon: 'bx bx-clipboard', path: '/patient/service-requests' },
                                { label: 'Appointments', icon: 'bx bx-calendar', path: '/patient/appointments' },
                                { label: 'Telehealth', icon: 'bx bx-video', path: '/patient/telehealth' },
                                { label: 'Collaborations', icon: 'bx bx-network-chart', path: '/patient/collaborations' },
                                { label: 'Billing', icon: 'bx bx-credit-card', path: '/patient/billing' },
                                { label: 'My EHR', icon: 'bx bx-file', path: '/patient/dashboard/ehr' },
                                { label: 'Medical Consents', icon: 'bx bx-check-shield', path: '/patient/consents' },
                            ].map((item, i) => (
                                <Link key={i} to={item.path} style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                                    textDecoration: 'none', color: 'var(--uzima-shell-text-soft)', fontSize: '0.82rem',
                                    transition: 'all 0.12s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = ACCENT_SOFT; e.currentTarget.style.color = ACCENT; }}
                                onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--uzima-shell-text-soft)'; }}
                                >
                                    <i className={item.icon} style={{ fontSize: '0.95rem', width: 18, textAlign: 'center' }}></i>
                                    {item.label}
                                    <i className="bx bx-chevron-right" style={{ marginLeft: 'auto', fontSize: '0.85rem', opacity: 0.4 }}></i>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Medical Record Sharing Notice */}
                <div className="sc-card" style={{ overflow: 'hidden' }}>
                    <div style={{ padding: '16px 24px', background: `linear-gradient(135deg, ${ACCENT} 0%, #14b8a6 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <i className="bx bx-shield-quarter" style={{ fontSize: '1.2rem', color: '#fff' }}></i>
                            <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.88rem' }}>Medical Record Sharing</span>
                        </div>
                        <Link to="/patient/consents" className="sc-btn sc-btn-ghost sc-btn-sm" style={{ textDecoration: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}>
                            View All
                        </Link>
                    </div>
                    <div className="sc-card-body" style={{ padding: '18px 24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 300px' }}>
                                <p style={{ fontWeight: 600, fontSize: '0.86rem', color: 'var(--uzima-shell-text)', marginBottom: 4 }}>Stay in control of your medical data</p>
                                <p style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)', margin: 0 }}>
                                    Review and manage requests from healthcare facilities to access your records. Your consent is required before any data is shared.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Link to="/patient/consents" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}>
                                    <i className="bx bx-check-shield"></i> Manage Consents
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* EHR Info Notice */}
                <div style={{ marginTop: 16, padding: '12px 20px', borderRadius: 12, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="bx bx-info-circle" style={{ fontSize: '1rem', color: '#2563eb' }}></i>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>
                        <b style={{ color: '#2563eb' }}>EHR Access</b> — You can view your electronic health records but cannot create or modify them. Only your associated healthcare facilities can manage your EHR records.
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PatientDashboard;